'use client'

import { useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'

import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { useGetProductsListQuery } from '../entities/productsList/queries'
import Breadcrumbs from '../features/market/ui/sections/BreadCrumbs'
import { GoodsDesktopList } from '../features/profile/ui/GoodsDesktopList'
import { GetProductsListResponse, SortOrder } from '../shared/api/generated'
import { useModal } from '../shared/hooks/useModal'
import FilterStorage from '../shared/storage/FilterStorage/FilterStorage'
import { BackArrowWithTitleLink } from '../shared/ui-kit/actual/BackArrowWithTitleLink'
import { FilterButton } from '../shared/ui-kit/actual/FilterButton'
import { LogoAllSharing } from '../shared/ui-kit/actual/LogoAllsharing'
import { PaginationComponent } from '../shared/ui-kit/actual/PaginationComponent'
import type { ProductListCardVariant } from '../shared/ui-kit/actual/ProductListCard'
import { ProductListHeader } from '../shared/ui-kit/actual/ProductListHeader'
import {
  MobileTabletFilterAction,
  ProductListingLayout,
} from '../shared/ui-kit/actual/ProductListingLayout'
import { SortButton } from '../shared/ui-kit/actual/SortButton'
import { AbsoluteLoader } from '../shared/ui-kit/actual/components/AbsoluteLoader'
import { MainHeader } from '../shared/ui-kit/actual/headers/MainHeader'
import { BasePageLayout } from '../shared/ui-kit/actual/layouts/BasePageLayout'
import { RightModal } from '../shared/ui-kit/actual/modals/RightModal'
import FilterWindowNew from '../shared/ui-kit/actual/modalsContent/FilterWindowNew'
import SortModalWindow from '../shared/ui-kit/actual/modalsContent/SortModalWindow'
import { MainSearchComponent } from '../shared/ui-kit/deprecated/MainSearchComponent'
import { FilterSubWrapper, MobileView } from '../shared/ui-kit/projectComponents'

interface SearchPageProps {
  initialProductsData?: GetProductsListResponse
  initialProductNameTerm?: null | string
  initialSectionId?: null | number
}

const ListingPageContent = styled.main`
  box-sizing: border-box;
  width: 100%;
  max-width: 1280px;
  margin-inline: auto;
`

const SearchPage = ({
  initialProductsData,
  initialProductNameTerm = null,
  initialSectionId = null,
}: SearchPageProps) => {
  const searchParams = useSearchParams()
  const productNameTerm = searchParams.get('searchValue')?.trim() || null
  const sectionId = Number(searchParams.get('sectionId')) || null
  const [itemsPerPage, setItemsPerPage] = useState(24)
  const [page, setPage] = useState(0)
  const [productListVariant, setProductListVariant] = useState<ProductListCardVariant>('card')

  const [openSortMenu, setOpenSortMenu] = useState(false)
  const { sorting } = useContext(FilterStorage)
  const [sortOrder, setSortOder] = useState<SortOrder>('asc')
  const toggleSortOrder = () => {
    setSortOder(sortOrder === 'asc' ? 'desc' : 'asc')
  }
  const { setViewModal, viewModal } = useModal()

  const handleFilterModalOpen = () => {
    setViewModal(prev => !prev)
  }
  const isInitialRequest =
    page === 0 &&
    itemsPerPage === 24 &&
    sortOrder === 'asc' &&
    productNameTerm === initialProductNameTerm &&
    sectionId === initialSectionId &&
    !sorting.dayPriceFrom &&
    !sorting.dayPriceTo &&
    !sorting.orderType &&
    !sorting.premiumOnly &&
    !sorting.ratingFrom

  const { data, isLoading } = useGetProductsListQuery(
    {
      dayPriceFrom: sorting.dayPriceFrom || null,
      dayPriceTo: sorting.dayPriceTo || null,
      orderType: sorting.orderType,
      premiumOnly: sorting.premiumOnly,
      productNameTerm,
      ratingFrom: sorting.ratingFrom,
      sectionId,
      skip: page * itemsPerPage,
      sortOrder: sortOrder,
      take: itemsPerPage,
    },
    { initialData: isInitialRequest ? initialProductsData : undefined }
  )

  const totalPages = useMemo(
    () => (data ? Math.ceil(data?.totalCount / itemsPerPage) : 1),
    [data, itemsPerPage]
  )

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [data])

  return (
    <>
      <BasePageLayout deferAuthContent>
        <AbsoluteLoader isLoading={isLoading} />
        <MainHeader />

        <ListingPageContent>
          <br />
          <Breadcrumbs />
          <ProductListHeader
            actions={
              <FilterSubWrapper>
                <SortButton
                  onClickSort={() => {
                    setOpenSortMenu(prev => !prev)
                  }}
                  selectedSort={sorting.orderType || 'byDefault'}
                  sortOrder={sortOrder}
                  toggleSort={toggleSortOrder}
                />
                <MobileTabletFilterAction>
                  <FilterButton onClickFilter={handleFilterModalOpen} />
                </MobileTabletFilterAction>
              </FilterSubWrapper>
            }
            onVariantChange={setProductListVariant}
            title={'Поиск'}
            variant={productListVariant}
          />
          <ProductListingLayout
            filters={<FilterWindowNew handleFilterModalOpen={() => undefined} isSearch />}
          >
            {data && (
              <GoodsDesktopList
                isLoading={isLoading}
                items={data?.items}
                messageNoProducts={'Ничего не нашлось. Попробуйте изменить запрос'}
                variant={productListVariant}
              />
            )}
            {data && (
              <PaginationComponent maxPages={totalPages} pageId={page} setPageId={setPage} />
            )}
          </ProductListingLayout>
        </ListingPageContent>
      </BasePageLayout>
      <RightModal mobileTabletOnly setViewModal={setViewModal} viewModal={viewModal}>
        <FilterWindowNew handleFilterModalOpen={handleFilterModalOpen} isSearch />
      </RightModal>
      <SortModalWindow
        handleMenu={() => {
          setOpenSortMenu(prev => !prev)
        }}
        openMenu={openSortMenu}
        selectedSort={sorting.orderType}
      />
    </>
  )
}

export default observer(SearchPage)
